/**
 * Reads the neighbourhood sentences the backend adds inside region.neighbourhood
 * (P1D-5540, API contract §3) and lays them over the view-model the tabs already
 * render. The backend owns wording, rounding, units, show/hide rules and
 * translation; nothing here builds or translates a sentence.
 *
 * Placement (contract §3.1): a stored object gets `<key>.sentence`, a stored
 * scalar gets `<key>_sentence`, and keyless items live under `<tab>.display`.
 */
import {
  BackendTabText,
  ClimateIndicator,
  FactRow,
  NeighbourhoodData,
  Sentiment,
  TrendDirection,
} from "../types";

const P = "propertypage.components.neighbourhood";

export interface BackendSentence {
  text: string;
  trend: TrendDirection;
  sentiment: Sentiment;
}

type AnyRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is AnyRecord =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const TRENDS: readonly string[] = ["up", "down", "neutral"];
const SENTIMENTS: readonly string[] = ["positive", "negative", "neutral"];

const asString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim().length > 0 ? value : undefined;

/** True only for a payload carrying version-1 backend sentences. */
export const hasBackendSentences = (neighbourhood: unknown): boolean => {
  if (!isRecord(neighbourhood)) return false;
  const meta = neighbourhood.sentences_meta;
  return isRecord(meta) && meta.version === 1;
};

const toSentence = (value: unknown): BackendSentence | undefined => {
  if (!isRecord(value)) return undefined;
  const text = asString(value.text);
  const { trend, sentiment } = value;
  if (!text || typeof trend !== "string" || typeof sentiment !== "string") return undefined;
  if (!TRENDS.includes(trend) || !SENTIMENTS.includes(sentiment)) return undefined;
  return { text, trend: trend as TrendDirection, sentiment: sentiment as Sentiment };
};

/** The sentence for `key` in `parent`: `parent[key].sentence`, else `parent[key + "_sentence"]`. */
export const readSentence = (parent: unknown, key: string): BackendSentence | undefined => {
  if (!isRecord(parent)) return undefined;
  const nested = isRecord(parent[key])
    ? toSentence((parent[key] as AnyRecord).sentence)
    : undefined;
  return nested ?? toSentence(parent[`${key}_sentence`]);
};

const toFact = (key: string, sentence: BackendSentence | undefined): FactRow | undefined =>
  sentence
    ? { key, text: sentence.text, trend: sentence.trend, sentiment: sentence.sentiment }
    : undefined;

const compactFacts = (facts: Array<FactRow | undefined>): FactRow[] =>
  facts.filter((fact): fact is FactRow => fact !== undefined);

const readDisplay = (tab: unknown): AnyRecord => {
  if (!isRecord(tab)) return {};
  return isRecord(tab.display) ? tab.display : {};
};

const readTabText = (display: AnyRecord, banner?: string): BackendTabText => {
  const card = isRecord(display.card) ? display.card : {};
  const fares: Record<string, string> = {};
  if (isRecord(display.fares)) {
    Object.entries(display.fares).forEach(([destination, fare]) => {
      const text = asString(fare);
      if (text) fares[destination] = text;
    });
  }
  return {
    card: {
      stat: asString(card.stat),
      subtext: asString(card.subtext),
      footerValue: asString(card.footer_value),
    },
    source: asString(display.source),
    banner,
    fares,
  };
};

const CLIMATE_LEVELS: readonly string[] = ["low", "moderate", "high"];
const CLIMATE_BADGES: ReadonlyArray<[string, string]> = [
  ["air_quality", `${P}.climate.badge.airQuality`],
  ["earthquake", `${P}.climate.badge.earthquake`],
  ["snowfall", `${P}.climate.badge.snowfall`],
];

const readClimateIndicators = (display: AnyRecord): ClimateIndicator[] => {
  const badges = isRecord(display.badges) ? display.badges : {};
  return CLIMATE_BADGES.flatMap(([badgeKey, labelKey]) => {
    const badge = badges[badgeKey];
    if (!isRecord(badge)) return [];
    const text = asString(badge.value);
    const { level } = badge;
    if (!text || typeof level !== "string" || !CLIMATE_LEVELS.includes(level)) return [];
    return [{ labelKey, value: { kind: "text", text }, level: level as ClimateIndicator["level"] }];
  });
};

/**
 * Returns `base` with every data-driven string replaced by the backend's. Hero
 * images, icons and Tolgee label keys come from `base`. A tab the backend sent
 * nothing for keeps no rows, so it fails its qualification gate and is hidden,
 * exactly as an item the backend omitted is hidden.
 */
export const applyBackendSentences = (
  base: NeighbourhoodData,
  neighbourhood: unknown,
): NeighbourhoodData => {
  if (!isRecord(neighbourhood)) return base;
  const cost = isRecord(neighbourhood.cost_of_living) ? neighbourhood.cost_of_living : undefined;
  const transit = cost && isRecord(cost.transit) ? cost.transit : undefined;
  const community = isRecord(neighbourhood.community) ? neighbourhood.community : undefined;
  const climate = isRecord(neighbourhood.climate) ? neighbourhood.climate : undefined;

  const result: NeighbourhoodData = { ...base };

  if (base.costOfLiving) {
    result.costOfLiving = {
      ...base.costOfLiving,
      facts: compactFacts([
        toFact(`${P}.costOfLiving.fact.rent`, readSentence(cost, "rent")),
        toFact(`${P}.costOfLiving.fact.transport`, readSentence(cost, "transportation")),
        toFact(`${P}.costOfLiving.fact.groceries`, readSentence(cost, "groceries")),
      ]),
      backend: readTabText(readDisplay(cost)),
    };
  }

  if (base.gettingAround) {
    const transitDisplay = readDisplay(transit);
    const banner = isRecord(transitDisplay.banner)
      ? asString(transitDisplay.banner.text)
      : undefined;
    result.gettingAround = {
      ...base.gettingAround,
      facts: compactFacts([
        toFact(
          `${P}.gettingAround.fact.walkability`,
          readSentence(transit, "walkability_score_percent_difference"),
        ),
        toFact(
          `${P}.gettingAround.fact.commute`,
          readSentence(transit, "commute_time_score_percent_difference"),
        ),
      ]),
      backend: readTabText(transitDisplay, banner),
    };
  }

  if (base.community) {
    const noise = readSentence(community, "noise_percent_difference");
    result.community = {
      ...base.community,
      facts: compactFacts([
        toFact(`${P}.community.fact.medianAge`, readSentence(community, "medianAge")),
        toFact(
          `${P}.community.fact.universities`,
          isRecord(community) ? toSentence(community.universities_sentence) : undefined,
        ),
        toFact(`${P}.community.fact.renters`, readSentence(community, "renterPercentage")),
      ]),
      backend: readTabText(readDisplay(community), noise?.text),
    };
  }

  if (base.climate) {
    const climateDisplay = readDisplay(climate);
    result.climate = {
      ...base.climate,
      facts: compactFacts([
        toFact(`${P}.climate.fact.winter`, readSentence(climate, "januaryAverageTemperature")),
        toFact(
          `${P}.climate.fact.summer`,
          readSentence(climate, "temperatureDifferenceFromNational"),
        ),
        toFact(`${P}.climate.fact.daylight`, readSentence(climate, "peakDaylight")),
      ]),
      indicators: readClimateIndicators(climateDisplay),
      backend: readTabText(climateDisplay),
    };
  }

  return result;
};
